import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api from '../../lib/api';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error';
  const { data, status } = error.response;
  return data?.detail || data?.message || `Request failed (${status})`;
};

const PurchaseReceipts = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingReceipt, setViewingReceipt] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) { navigate('/login'); return; }
    setUser(JSON.parse(storedUser));
    fetchReceipts();
  }, [navigate]);

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/purchase-receipts');
      setReceipts(res.data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (receipt) => {
    if (!window.confirm(`Confirm receipt ${receipt.receipt_number}? This will ADD stock to inventory. This action cannot be undone easily.`)) return;
    try {
      const res = await api.post(`/purchase-receipts/${receipt.id}/confirm`);
      alert(res.data.message || 'Receipt confirmed, stock updated');
      fetchReceipts();
    } catch (err) {
      alert(getErrorMessage(err));
    }
  };

  const handleCancel = async (receipt) => {
    if (!window.confirm(`Cancel receipt ${receipt.receipt_number}?${receipt.status === 'confirmed' ? ' Stock will be reversed.' : ''}`)) return;
    try {
      await api.post(`/purchase-receipts/${receipt.id}/cancel`);
      alert('Receipt cancelled');
      fetchReceipts();
    } catch (err) {
      alert(getErrorMessage(err));
    }
  };

  const handleView = async (receipt) => {
    try {
      const res = await api.get(`/purchase-receipts/${receipt.id}`);
      setViewingReceipt(res.data);
    } catch (err) {
      alert('Failed to load receipt details');
    }
  };

  const statusColors = {
    draft: 'bg-[#F5F5F5] text-[#6b7280]',
    confirmed: 'bg-[#DBEAFE] text-[#1E40AF]',
    cancelled: 'bg-[#FEE2E2] text-[#991B1B]',
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="receipts-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="receipts-title">Purchase Receipts (GRN)</h1>
            <p className="text-sm text-[#6b7280] mt-1">Create receipts from Purchase Orders. Confirm to add stock.</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">Loading...</div>
          ) : receipts.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center">
              <p className="text-[#6b7280]">No receipts found. Create receipts from Purchase Orders page.</p>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="receipts-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr>
                    <th className="py-3 px-4">GRN #</th>
                    <th className="py-3 px-4">PO #</th>
                    <th className="py-3 px-4">Supplier</th>
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Items</th>
                    <th className="py-3 px-4">Amount</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r) => (
                    <tr key={r.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                      <td className="py-3 px-4 font-medium text-[#1a1a2e]">{r.receipt_number}</td>
                      <td className="py-3 px-4 text-[#6b7280]">{r.purchase_order?.po_number || '-'}</td>
                      <td className="py-3 px-4 text-[#1a1a2e]">{r.supplier?.name || 'N/A'}</td>
                      <td className="py-3 px-4 text-[#1a1a2e]">{r.receipt_date ? new Date(r.receipt_date).toLocaleDateString() : '-'}</td>
                      <td className="py-3 px-4 text-[#6b7280]">{r.total_items}</td>
                      <td className="py-3 px-4 font-medium text-[#1a1a2e]">Rs. {parseFloat(r.total_amount).toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${statusColors[r.status] || 'bg-gray-100'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => handleView(r)} className="text-[#1E40AF] hover:underline text-xs font-medium flex items-center gap-1" data-testid={`view-receipt-${r.id}`}>
                            <Eye className="w-3 h-3" /> View
                          </button>
                          {r.status === 'draft' && (
                            <button onClick={() => handleConfirm(r)} className="text-[#1E40AF] hover:underline text-xs font-medium" data-testid={`confirm-receipt-${r.id}`}>
                              Confirm
                            </button>
                          )}
                          {r.status !== 'cancelled' && (
                            <button onClick={() => handleCancel(r)} className="text-[#EF4444] hover:underline text-xs font-medium" data-testid={`cancel-receipt-${r.id}`}>
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {viewingReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="receipt-detail-modal">
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-[#e5e7eb] flex justify-between items-center shrink-0">
              <h2 className="text-xl font-semibold">{viewingReceipt.receipt_number}</h2>
              <button onClick={() => setViewingReceipt(null)} className="text-[#6b7280] hover:text-[#1a1a2e]" data-testid="close-receipt-detail">
                <span className="text-lg">&times;</span>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div><span className="font-medium">PO:</span> {viewingReceipt.purchase_order?.po_number}</div>
                <div><span className="font-medium">Status:</span> <span className={`px-2 py-1 rounded-sm text-xs font-medium ${statusColors[viewingReceipt.status]}`}>{viewingReceipt.status}</span></div>
                <div><span className="font-medium">Supplier:</span> {viewingReceipt.supplier?.name}</div>
                <div><span className="font-medium">Date:</span> {viewingReceipt.receipt_date ? new Date(viewingReceipt.receipt_date).toLocaleDateString() : '-'}</div>
              </div>
              <h3 className="font-semibold mb-2 text-sm">Received Items</h3>
              <table className="w-full text-sm border border-[#e5e7eb] rounded-sm">
                <thead className="bg-[#f9fafb]">
                  <tr>
                    <th className="py-2 px-3 text-left">Product</th>
                    <th className="py-2 px-3 text-center">Qty Received</th>
                    <th className="py-2 px-3 text-right">Unit Cost</th>
                    <th className="py-2 px-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingReceipt.items?.map((item, i) => (
                    <tr key={i} className="border-t border-[#e5e7eb]">
                      <td className="py-2 px-3">{item.product_name} ({item.product_sku})</td>
                      <td className="py-2 px-3 text-center">{item.quantity_received}</td>
                      <td className="py-2 px-3 text-right">Rs. {parseFloat(item.unit_cost).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right">Rs. {parseFloat(item.total).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-[#e5e7eb] flex justify-end shrink-0">
              <button onClick={() => setViewingReceipt(null)} className="px-4 py-2 border border-[#e5e7eb] rounded-sm text-[#1a1a2e] hover:bg-[#F5F5F5] font-medium">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseReceipts;
